import { Search } from '@styled-icons/boxicons-regular/Search';
import debounce from 'lodash.debounce';
import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Loader from '@/components/loader';
import { alpha } from '@/themes';
import { flagForCountryCode } from '@/utils';
import { getFallbackImage } from '@/utils/images';

const Container = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  position: relative;
  width: calc(100% - 2.75em);
`;

const SearchIcon = styled(Search)`
  color: var(--ink-subtle);
  left: 1em;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(calc(-50% + 1px));
`;

const Input = styled.input.attrs({ type: 'search' })`
  appearance: none;
  background: none;
  border: none;
  box-sizing: border-box;
  font-family: ${({ theme }) => theme.fonts.primary};
  color: ${({ theme }) => theme.colors.typography.primary};
  caret-color: ${({ theme }) => theme.colors.typography.primary};
  display: block;
  flex-grow: 1;
  font-size: 1.1em;
  height: 100%;
  margin: 0 .5em 0 0;
  outline: none;
  padding: .65em 1em .65em 2.75em;
  transition: all 250ms;

  &::placeholder {
    color: var(--muted-foreground);
    opacity: 1;
  }

  &:focus, &:hover {
    outline: none;
  }
`;

const Suggestions = styled.ul`
  border-radius: 0.5em;
  list-style: none;
  margin: 0;
  max-height: 20em;
  overflow-y: auto;
  padding: 0;
  position: absolute;
  top: 100%;
  width: 100%;

  ${({ theme }) => css`
    background-color: ${theme.colors.background.card};
    box-shadow: ${theme.shadows.primary};

    li + li {
      border-top: 1px solid var(--border);
    }

    a {
      color: ${theme.colors.typography.primary};
      cursor: pointer;

      &[aria-selected='true'] {
        background-color: ${alpha(theme.colors.primary, 5)};
      }
    }
  `};
`;

const suggestionRow = css`
  align-items: center;
  display: flex;
  padding: 1em 1.25em;
`;

const Suggestion = styled.li`
  ${suggestionRow};

  .loader {
    margin-right: 1em;
  }
`;

const SuggestionLink = styled(Link)`
  ${suggestionRow};
`;

const SuggestionName = styled.span`
  flex-grow: 1;
  margin-left: 1em;
  text-align: left;
`;

const SearchField = ({
  className = '',
  isLoadingSuggestions,
  onBlur,
  onChange = () => {},
  onEnter,
  onFocus,
  onSuggestionClick,
  search,
  searchTerm,
  setSearchTerm,
  suggestions,
}) => {
  const searchRef = useRef(search);
  const onChangeRef = useRef(onChange);
  searchRef.current = search;
  onChangeRef.current = onChange;

  const doSearch = useMemo(
    () =>
      debounce((event) => {
        searchRef.current(event);
      }, 300),
    []
  );
  const doDebouncedChange = useMemo(
    () =>
      debounce((event) => {
        onChangeRef.current(event);
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      doSearch.cancel();
      doDebouncedChange.cancel();
    };
  }, [doSearch, doDebouncedChange]);

  const options = Array.isArray(suggestions) ? suggestions : [];
  const t = useTranslations('searchField');
  const [isFocused, setIsFocused] = useState(false);
  // Escape hides the list until the next keystroke or focus.
  const [isDismissed, setIsDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const optionRefs = useRef([]);
  // Set by the arrow keys only, so hovering never scrolls the list.
  const scrollToActive = useRef(false);
  const listboxId = useId();
  const optionId = (index) => `${listboxId}-option-${index}`;

  const showSuggestions = !isDismissed && (isLoadingSuggestions || options.length > 0);
  const activeOption = showSuggestions && activeIndex >= 0 ? activeIndex : -1;

  // A new result set starts with nothing highlighted.
  const [prevSuggestions, setPrevSuggestions] = useState(suggestions);
  if (suggestions !== prevSuggestions) {
    setPrevSuggestions(suggestions);
    setActiveIndex(-1);
  }

  // After render, since an arrow key can reopen a dismissed list.
  useEffect(() => {
    if (!scrollToActive.current) return;
    scrollToActive.current = false;
    optionRefs.current[activeOption]?.scrollIntoView({ block: 'nearest' });
  }, [activeOption]);

  const handleSearch = useCallback(
    (e) => {
      setSearchTerm(e);
      setIsDismissed(false);
      setActiveIndex(-1);

      if (e.target.value.trim().length >= 3 || !e.target.value.length) {
        doSearch(e);
        doDebouncedChange(e);
      }
    },
    [doSearch, setSearchTerm, doDebouncedChange]
  );

  const moveActive = (index) => {
    setActiveIndex(index);
    scrollToActive.current = true;
  };

  // Arrows cycle through the suggestions and back to the input (-1).
  const handleKeyDown = (e) => {
    const count = options.length;

    switch (e.key) {
      case 'ArrowDown':
        if (!count) return;
        e.preventDefault();
        setIsDismissed(false);
        moveActive(activeOption + 1 < count ? activeOption + 1 : -1);
        break;
      case 'ArrowUp':
        if (!count) return;
        e.preventDefault();
        setIsDismissed(false);
        moveActive(activeOption > -1 ? activeOption - 1 : count - 1);
        break;
      case 'Escape':
        if (!showSuggestions) return;
        // Keep the native search input from clearing the query as well.
        e.preventDefault();
        setIsDismissed(true);
        setActiveIndex(-1);
        break;
      case 'Enter':
        if (e.nativeEvent.isComposing) return;
        if (activeOption >= 0) {
          e.preventDefault();
          // Goes through the link's own click handling and client navigation.
          optionRefs.current[activeOption]?.click();
        } else if (e.target.value.trim().length >= 3 && typeof onEnter === 'function') {
          onEnter();
        }
        break;
      default:
    }
  };

  const handleClick = (suggestion) => () => {
    if (typeof onSuggestionClick === 'function') {
      onSuggestionClick(suggestion);
    }
  };

  const handleBlur = () => {
    if (typeof onBlur === 'function') onBlur();
    setIsFocused(false);
  };

  const handleFocus = () => {
    if (typeof onFocus === 'function') onFocus();
    setIsFocused(true);
    setIsDismissed(false);
  };

  return (
    <Container
      className={`search-field ${className} ${isFocused ? 'search-field--focused' : 'search-field--blurred'}`}
    >
      <SearchIcon size={24} />
      <Input
        aria-activedescendant={activeOption >= 0 ? optionId(activeOption) : undefined}
        aria-autocomplete="list"
        aria-controls={showSuggestions ? listboxId : undefined}
        aria-expanded={showSuggestions}
        onBlur={handleBlur}
        onChange={handleSearch}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={t('placeholder')}
        role="combobox"
        value={searchTerm}
      />
      {showSuggestions && (
        <Suggestions id={listboxId} onMouseLeave={() => setActiveIndex(-1)} role="listbox">
          {isLoadingSuggestions && (
            <Suggestion aria-disabled="true" role="option">
              <Loader mode="inline" size={16} /> {t('loading')}
            </Suggestion>
          )}
          {options.map((suggestion, index) => (
            <li key={suggestion.id} role="none">
              <SuggestionLink
                aria-selected={index === activeOption}
                href={`/brands/${suggestion.country.slug}/${suggestion.slug}`}
                id={optionId(index)}
                onClick={handleClick(suggestion)}
                onMouseMove={() => setActiveIndex(index)}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                role="option"
                tabIndex={-1}
              >
                <Image
                  alt=""
                  fallback={getFallbackImage(suggestion.type)}
                  height={32}
                  src={suggestion.imageUrl}
                  unoptimized={true}
                  width={48}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                  }}
                />
                <SuggestionName>{suggestion.name}</SuggestionName>
                {flagForCountryCode(suggestion.countryId)}
              </SuggestionLink>
            </li>
          ))}
        </Suggestions>
      )}
    </Container>
  );
};

export default observer(SearchField);
